import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';

export enum MainPalette {
  Bridebook = 'bridebook',
  Foliage = 'foliage',
  Roses = 'roses',
  Whimsical = 'whimsical',
  Glamour = 'glamour',
  Sketches = 'sketches',
  Timeless = 'timeless',
  Blossom = 'blossom',
  Amore = 'amore',
}

export const Palette = {
  ...MainPalette,
  BridebookGreen: 'bridebook-green',
  BridebookBlush: 'bridebook-blush',
  FoliageOrange: 'foliage-orange',
  FoliageBlue: 'foliage-blue',
  RosesPlum: 'roses-plum',
  RosesBrown: 'roses-brown',
  WhimsicalGreen: 'whimsical-green',
  WhimsicalBeige: 'whimsical-beige',
  GlamourNavy: 'glamour-navy',
  GlamourChampagne: 'glamour-champagne',
  SketchesGreen: 'sketches-green',
  SketchesLilac: 'sketches-lilac',
} as const;

export type Palette = (typeof Palette)[keyof typeof Palette];

export const PALETTE_COLORWAYS_MAP: Record<MainPalette, Palette[]> = {
  [MainPalette.Bridebook]: [Palette.Bridebook, Palette.BridebookGreen, Palette.BridebookBlush],
  [MainPalette.Roses]: [Palette.Roses, Palette.RosesPlum, Palette.RosesBrown],
  [MainPalette.Foliage]: [Palette.Foliage, Palette.FoliageBlue, Palette.FoliageOrange],
  [MainPalette.Whimsical]: [Palette.Whimsical, Palette.WhimsicalBeige, Palette.WhimsicalGreen],
  [MainPalette.Sketches]: [Palette.Sketches, Palette.SketchesGreen, Palette.SketchesLilac],
  [MainPalette.Glamour]: [Palette.Glamour, Palette.GlamourNavy, Palette.GlamourChampagne],
  [MainPalette.Timeless]: [Palette.Timeless],
  [MainPalette.Blossom]: [Palette.Blossom],
  [MainPalette.Amore]: [Palette.Amore],
};

export const isPalette = (value: string): value is Palette =>
  Object.values(Palette).includes(value as Palette);

export interface PaletteVariables {
  // Surface colors
  '--ww-palette-surface-primary': string;
  '--ww-palette-surface-overlay': string;
  '--ww-palette-accent': string;
  '--ww-palette-surface-secondary': string;

  // Page component colors
  '--ww-palette-cta': string;
  '--ww-palette-cta-hover': string;
  '--ww-palette-cta-active': string;
  '--ww-palette-divider': string;

  // Text colors
  '--ww-palette-text-primary': string;
  '--ww-palette-text-secondary': string;
  '--ww-palette-text-cta': string;
}

export const PALETTE_VARIABLES: Record<Palette, PaletteVariables> = {
  [Palette.Bridebook]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-indigo-crush-10)',
    '--ww-palette-surface-overlay': 'var(--color-white-a60)',
    '--ww-palette-accent': 'var(--color-indigo-crush-20)',
    '--ww-palette-surface-secondary': 'var(--color-indigo-crush-10)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-indigo-crush)',
    '--ww-palette-cta-hover': 'var(--color-indigo-crush-80)',
    '--ww-palette-cta-active': 'var(--color-indigo-crush-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-space-75)',
    '--ww-palette-text-secondary': 'var(--color-space-75)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },
  [Palette.BridebookGreen]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-sage-20)',
    '--ww-palette-surface-overlay': 'var(--color-white-a40)',
    '--ww-palette-accent': 'var(--color-sage-40)',
    '--ww-palette-surface-secondary': 'var(--color-sage-20)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-sage)',
    '--ww-palette-cta-hover': 'var(--color-sage-80)',
    '--ww-palette-cta-active': 'var(--color-sage-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },
  [Palette.BridebookBlush]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-blush-20)',
    '--ww-palette-surface-overlay': 'var(--color-white-a60)',
    '--ww-palette-accent': 'var(--color-blush-40)',
    '--ww-palette-surface-secondary': 'var(--color-blush-20)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-blush)',
    '--ww-palette-cta-hover': 'var(--color-blush-80)',
    '--ww-palette-cta-active': 'var(--color-blush-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },
  [Palette.Roses]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-cream)',
    '--ww-palette-surface-overlay': 'var(--color-white-a60)',
    '--ww-palette-accent': 'transparent',
    '--ww-palette-surface-secondary': 'var(--color-cream)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-baby-pink)',
    '--ww-palette-cta-hover': 'var(--color-baby-pink-80)',
    '--ww-palette-cta-active': 'var(--color-baby-pink-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-brown)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-brown)',
    '--ww-palette-text-cta': 'var(--color-space-75)',
  },
  [Palette.RosesPlum]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-plum)',
    '--ww-palette-surface-overlay': 'var(--color-white-a40)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-plum)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-dark-plum)',
    '--ww-palette-cta-hover': 'var(--color-dark-plum-80)',
    '--ww-palette-cta-active': 'var(--color-dark-plum-120)',
    '--ww-palette-divider': 'var(--color-cloudy)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },
  [Palette.RosesBrown]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-dusty-brown)',
    '--ww-palette-surface-overlay': 'var(--color-neutrals-black-a20)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-dusty-brown)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-mauve-80)',
    '--ww-palette-cta-hover': 'var(--color-mauve-60)',
    '--ww-palette-cta-active': 'var(--color-whimsical-mauve)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-white)',
    '--ww-palette-text-secondary': 'var(--color-cloudy)',
    '--ww-palette-text-cta': 'var(--color-dusty-brown)',
  },

  [Palette.Foliage]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-foliage-eucalyptus-light)',
    '--ww-palette-surface-overlay': 'var(--color-white-a80)',
    '--ww-palette-accent': 'transparent',
    '--ww-palette-surface-secondary': 'var(--color-foliage-eucalyptus-light)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-foliage-eucalyptus)',
    '--ww-palette-cta-hover': 'var(--color-foliage-eucalyptus-80)',
    '--ww-palette-cta-active': 'var(--color-foliage-eucalyptus-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-brown)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-brown)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },

  [Palette.FoliageOrange]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-orange-light)',
    '--ww-palette-surface-overlay': 'var(--color-white-a60)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-orange-light)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-orange)',
    '--ww-palette-cta-hover': 'var(--color-orange-80)',
    '--ww-palette-cta-active': 'var(--color-orange-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },
  [Palette.FoliageBlue]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-blue-light)',
    '--ww-palette-surface-overlay': 'var(--color-white-a60)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-blue-light)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-blue)',
    '--ww-palette-cta-hover': 'var(--color-blue-80)',
    '--ww-palette-cta-active': 'var(--color-blue-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },
  [Palette.Whimsical]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-whimsical-mauve)',
    '--ww-palette-surface-overlay': 'var(--color-white-a40)',
    '--ww-palette-accent': 'transparent',
    '--ww-palette-surface-secondary': 'var(--color-whimsical-mauve)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-whimsical-pink-joy)',
    '--ww-palette-cta-hover': 'var(--color-whimsical-pink-joy-80)',
    '--ww-palette-cta-active': 'var(--color-whimsical-pink-joy-120)',
    '--ww-palette-divider': 'var(--color-white)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-whimsical-oxblood)',
    '--ww-palette-text-secondary': 'var(--color-whimsical-mulberry)',
    '--ww-palette-text-cta': 'var(--color-whimsical-mulberry)',
  },
  [Palette.WhimsicalGreen]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-emerald)',
    '--ww-palette-surface-overlay': 'var(--color-white-a10)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-emerald)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-dark-emerald)',
    '--ww-palette-cta-hover': 'var(--color-dark-emerald-80)',
    '--ww-palette-cta-active': 'var(--color-dark-emerald-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-white)',
    '--ww-palette-text-secondary': 'var(--color-cloudy)',
    '--ww-palette-text-cta': 'var(--color-cloudy)',
  },
  [Palette.WhimsicalBeige]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-porcelain)',
    '--ww-palette-surface-overlay': 'var(--color-white-a80)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-porcelain)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-sand)',
    '--ww-palette-cta-hover': 'var(--color-sand-80)',
    '--ww-palette-cta-active': 'var(--color-sand-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-dark-brown)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-dark-brown)',
    '--ww-palette-text-cta': 'var(--color-neutrals-slate)',
  },
  [Palette.Sketches]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-cloudy)',
    '--ww-palette-surface-overlay': 'var(--color-white-a60)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-cloudy)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-dusty-rose)',
    '--ww-palette-cta-hover': 'var(--color-dusty-rose-80)',
    '--ww-palette-cta-active': 'var(--color-dusty-rose-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },
  [Palette.SketchesGreen]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-sketches-green)',
    '--ww-palette-surface-overlay': 'var(--color-white-a10)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-sketches-green)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-white)',
    '--ww-palette-cta-hover': 'var(--color-white)',
    '--ww-palette-cta-active': 'var(--color-cloudy)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-sketches-dark-green)',
    '--ww-palette-text-secondary': 'var(--color-cloudy)',
    '--ww-palette-text-cta': 'var(--color-sketches-dark-green)',
  },
  [Palette.SketchesLilac]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-lilac)',
    '--ww-palette-surface-overlay': 'var(--color-white-a30)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-lilac)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-dusty-rose)',
    '--ww-palette-cta-hover': 'var(--color-dusty-rose-80)',
    '--ww-palette-cta-active': 'var(--color-dusty-rose-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-black)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-brown)',
    '--ww-palette-text-cta': 'var(--color-white)',
  },
  [Palette.Glamour]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-crimson)',
    '--ww-palette-surface-overlay': 'var(--color-bordeaux)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-crimson)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-gold-cta)',
    '--ww-palette-cta-hover': 'var(--color-gold-80)',
    '--ww-palette-cta-active': 'var(--color-gold-120)',
    '--ww-palette-divider': 'var(--color-baby-pink)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-white)',
    '--ww-palette-text-secondary': 'var(--color-baby-pink)',
    '--ww-palette-text-cta': 'var(--color-crimson)',
  },
  [Palette.GlamourNavy]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-navy)',
    '--ww-palette-surface-overlay': 'var(--color-umber-a20)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-navy)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-gold-cta)',
    '--ww-palette-cta-hover': 'var(--color-gold-80)',
    '--ww-palette-cta-active': 'var(--color-gold-120)',
    '--ww-palette-divider': 'var(--color-gold-80)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-white)',
    '--ww-palette-text-secondary': 'var(--color-navy-light)',
    '--ww-palette-text-cta': 'var(--color-navy)',
  },
  [Palette.GlamourChampagne]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-champagne)',
    '--ww-palette-surface-overlay': 'var(--color-umber-a10)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-champagne)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-gold-cta)',
    '--ww-palette-cta-hover': 'var(--color-gold-80)',
    '--ww-palette-cta-active': 'var(--color-gold-120)',
    '--ww-palette-divider': 'var(--color-gold-80)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-black)',
    '--ww-palette-text-secondary': 'var(--color-neutrals-slate)',
    '--ww-palette-text-cta': 'var(--color-champagne)',
  },
  [Palette.Timeless]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-space)',
    '--ww-palette-surface-overlay': 'var(--color-dust)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-dust)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-dove)',
    '--ww-palette-cta-hover': 'var(--color-dove-80)',
    '--ww-palette-cta-active': 'var(--color-dove-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-black)',
    '--ww-palette-text-secondary': 'var(--color-space)',
    '--ww-palette-text-cta': 'var(--color-space)',
  },
  [Palette.Blossom]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-cream)',
    '--ww-palette-surface-overlay': 'var(--color-white-a80)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-white-a80)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-beige)',
    '--ww-palette-cta-hover': 'var(--color-beige-80)',
    '--ww-palette-cta-active': 'var(--color-beige-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-space)',
    '--ww-palette-text-secondary': 'var(--color-space-75)',
    '--ww-palette-text-cta': 'var(--color-space)',
  },
  [Palette.Amore]: {
    // Surface colors
    '--ww-palette-surface-primary': 'var(--color-white)',
    '--ww-palette-surface-overlay': 'var(--color-cloudy)',
    '--ww-palette-accent': 'var(--color-invisible)',
    '--ww-palette-surface-secondary': 'var(--color-cloudy)',

    // Page component colors
    '--ww-palette-cta': 'var(--color-baby-pink)',
    '--ww-palette-cta-hover': 'var(--color-baby-pink-80)',
    '--ww-palette-cta-active': 'var(--color-baby-pink-120)',
    '--ww-palette-divider': 'var(--color-space-40)',

    // Text colors
    '--ww-palette-text-primary': 'var(--color-space)',
    '--ww-palette-text-secondary': 'var(--color-space-75)',
    '--ww-palette-text-cta': 'var(--color-space)',
  },
} as const;

export const ASSET_PREFIX = `${IMAGES_BASE_URL}/assets/wedding-websites-imgs`;

export const PALETTE_ACCENTS = {
  [Palette.Roses]: `${ASSET_PREFIX}/palette-roses-accent.png`,
  [Palette.RosesPlum]: `${ASSET_PREFIX}/palette-roses-plum-accent.png`,
  [Palette.RosesBrown]: `${ASSET_PREFIX}/palette-roses-brown-accent.png`,
  [Palette.Whimsical]: `${ASSET_PREFIX}/palette-whimsical-black-accent.png`,
  [Palette.WhimsicalBeige]: `${ASSET_PREFIX}/palette-whimsical-black-accent.png`,
  [Palette.WhimsicalGreen]: `${ASSET_PREFIX}/palette-whimsical-white-accent.png`,
  [Palette.Foliage]: `${ASSET_PREFIX}/palette-foliage-green-accent.png`,
  [Palette.FoliageOrange]: `${ASSET_PREFIX}/palette-foliage-orange-accent.png`,
  [Palette.FoliageBlue]: `${ASSET_PREFIX}/palette-foliage-blue-accent.png`,
  [Palette.Timeless]: `${ASSET_PREFIX}/palette-timeless-accent.png`,
  [Palette.Blossom]: `${ASSET_PREFIX}/palette-blossom-accent.png`,
  [Palette.Amore]: `${ASSET_PREFIX}/palette-amore-accent.png`,
} as const;

export const SKETCHES_ACCENTS = {
  [Palette.Sketches]: {
    desktopLeft: `${ASSET_PREFIX}/palette-sketches-accent-desktop-left.png`,
    desktopRight: `${ASSET_PREFIX}/palette-sketches-accent-desktop-right.png`,
    mobileLeft: `${ASSET_PREFIX}/palette-sketches-accent-mobile-left.png`,
    mobileRight: `${ASSET_PREFIX}/palette-sketches-accent-mobile-right.png`,
  },
  [Palette.SketchesGreen]: {
    desktopLeft: `${ASSET_PREFIX}/palette-sketches-green-accent-desktop-left.png`,
    desktopRight: `${ASSET_PREFIX}/palette-sketches-green-accent-desktop-right.png`,
    mobileLeft: `${ASSET_PREFIX}/palette-sketches-green-accent-mobile-left.png`,
    mobileRight: `${ASSET_PREFIX}/palette-sketches-green-accent-mobile-right.png`,
  },
  [Palette.SketchesLilac]: {
    desktopLeft: `${ASSET_PREFIX}/palette-sketches-accent-desktop-left.png`,
    desktopRight: `${ASSET_PREFIX}/palette-sketches-accent-desktop-right.png`,
    mobileLeft: `${ASSET_PREFIX}/palette-sketches-accent-mobile-left.png`,
    mobileRight: `${ASSET_PREFIX}/palette-sketches-accent-mobile-right.png`,
  },
};

export const GLAMOUR_ACCENTS = {
  desktop: `${ASSET_PREFIX}/palette-glamour-accent.png`,
  mobile: `${ASSET_PREFIX}/palette-glamour-accent-mobile.png`,
};

// Tiles background used for setting the pallette in the design panel
export const PALETTE_TILES: Record<Palette, string> = {
  [Palette.Bridebook]: `${ASSET_PREFIX}/tiles/bridebook.png`,
  [Palette.BridebookGreen]: `${ASSET_PREFIX}/tiles/bridebook-green.png`,
  [Palette.BridebookBlush]: `${ASSET_PREFIX}/tiles/bridebook-blush.png`,
  [Palette.Roses]: `${ASSET_PREFIX}/tiles/roses.png`,
  [Palette.RosesPlum]: `${ASSET_PREFIX}/tiles/roses-plum.png`,
  [Palette.RosesBrown]: `${ASSET_PREFIX}/tiles/roses-brown.png`,
  [Palette.Foliage]: `${ASSET_PREFIX}/tiles/foliage.png`,
  [Palette.FoliageOrange]: `${ASSET_PREFIX}/tiles/foliage-orange.png`,
  [Palette.FoliageBlue]: `${ASSET_PREFIX}/tiles/foliage-blue.png`,
  [Palette.Whimsical]: `${ASSET_PREFIX}/tiles/whimsical-black.png`,
  [Palette.WhimsicalBeige]: `${ASSET_PREFIX}/tiles/whimsical-black.png`,
  [Palette.WhimsicalGreen]: `${ASSET_PREFIX}/tiles/whimsical-white.png`,
  [Palette.Sketches]: `${ASSET_PREFIX}/tiles/sketches.png`,
  [Palette.SketchesGreen]: `${ASSET_PREFIX}/tiles/sketches-green.png`,
  [Palette.SketchesLilac]: `${ASSET_PREFIX}/tiles/sketches.png`,
  [Palette.Glamour]: `${ASSET_PREFIX}/tiles/glamour.png`,
  [Palette.GlamourNavy]: `${ASSET_PREFIX}/tiles/glamour.png`,
  [Palette.GlamourChampagne]: `${ASSET_PREFIX}/tiles/glamour.png`,
  [Palette.Timeless]: `${ASSET_PREFIX}/tiles/timeless.png`,
  [Palette.Blossom]: `${ASSET_PREFIX}/tiles/blossom.png`,
  [Palette.Amore]: `${ASSET_PREFIX}/tiles/amore-2.png`,
} as const;
